import {
  Company,
  CurrencyDropDown,
  DocumentType,
  InvoiceExpenseType,
  PaymentChannel
} from 'openapi';

import {
  DOCUMENT_TYPES_TRANSLATION_KEYS,
  PAYMENT_CHANNELS_TRANSLATION_KEYS
} from 'utils/constants/invoices';
import { TranslateFunction } from 'utils/interfaces/Translations';

import { formatMoney } from './moneyHelper';

const SEPARATOR = ' · ';

interface InitiationSummaryParams {
  companyId?: number;
  documentType?: DocumentType;
  allCompanies: Company[];
}

export const getInitiationSummary = (
  { companyId, documentType, allCompanies }: InitiationSummaryParams,
  translate: TranslateFunction
): string => {
  const company = allCompanies.find((c) => c.id === companyId);

  const receiverSegment = company?.name
    ? { value: company.name, isMissing: false }
    : { value: translate('sections.receiverMissing'), isMissing: true };

  const documentTypeSegment = documentType
    ? {
        value: translate(DOCUMENT_TYPES_TRANSLATION_KEYS[documentType]),
        isMissing: false
      }
    : { value: translate('sections.documentTypeMissing'), isMissing: true };

  const segments = [receiverSegment, documentTypeSegment];
  const hasRealValue = segments.some((segment) => !segment.isMissing);

  const valuesToShow = hasRealValue
    ? segments.filter((segment) => !segment.isMissing).map((s) => s.value)
    : segments.map((s) => s.value);

  return valuesToShow.join(SEPARATOR);
};

interface CounterpartySummaryParams {
  counterpartyId?: number;
  counterpartyName?: string;
  counterpartyVatNumber?: string;
  counterpartyRegistrationNumber?: string;
}

export const getCounterpartySummary = (
  {
    counterpartyId,
    counterpartyName,
    counterpartyVatNumber,
    counterpartyRegistrationNumber
  }: CounterpartySummaryParams,
  translate: TranslateFunction
): string => {
  if (!counterpartyId && !counterpartyName) {
    return translate('sections.noCounterpartySelected');
  }

  const segments: string[] = [];

  if (counterpartyName) {
    segments.push(counterpartyName);
  }

  if (counterpartyVatNumber) {
    segments.push(`(${counterpartyVatNumber})`);
  }

  if (counterpartyRegistrationNumber) {
    segments.push(`(${counterpartyRegistrationNumber})`);
  }

  return segments.join(' ');
};

interface ReimbursementSummaryParams {
  isReimbursement?: boolean;
  reimbursementCounterpartyName?: string;
}

export const getReimbursementSummary = (
  {
    isReimbursement,
    reimbursementCounterpartyName
  }: ReimbursementSummaryParams,
  translate: TranslateFunction
): string => {
  if (!isReimbursement) {
    return translate('sections.notAReimbursement');
  }

  if (!reimbursementCounterpartyName) {
    return translate('sections.recipientNotSpecified');
  }

  return reimbursementCounterpartyName;
};

interface DocumentPaymentStatusSummaryParams {
  isPrepaid?: boolean;
  paymentChannel?: PaymentChannel;
}

export const getDocumentPaymentStatusSummary = (
  { isPrepaid, paymentChannel }: DocumentPaymentStatusSummaryParams,
  translate: TranslateFunction
): string => {
  if (!isPrepaid) {
    return translate('sections.notPaidYet');
  }

  const channel = paymentChannel
    ? translate(PAYMENT_CHANNELS_TRANSLATION_KEYS[paymentChannel])
    : null;

  return channel
    ? `${translate('sections.alreadyPaid')}${SEPARATOR}${channel}`
    : translate('sections.alreadyPaid');
};

interface DocumentDataSummaryParams {
  invoiceNumber?: string;
  invoiceDate?: string;
  expenseType?: number | string;
  invoiceAmount?: number | string;
  currencyId?: number;
  expenseTypeOptions: InvoiceExpenseType[];
  currencies: CurrencyDropDown[];
}

export const getDocumentDataSummary = (
  {
    invoiceNumber,
    invoiceDate,
    expenseType,
    invoiceAmount,
    currencyId,
    expenseTypeOptions,
    currencies
  }: DocumentDataSummaryParams,
  translate: TranslateFunction
): string => {
  const numberSegment = invoiceNumber
    ? { value: `#${invoiceNumber}`, isMissing: false }
    : { value: translate('sections.numberMissing'), isMissing: true };

  const dateSegment = invoiceDate
    ? { value: invoiceDate, isMissing: false }
    : { value: translate('sections.dateMissing'), isMissing: true };

  const expenseTypeName = expenseType
    ? expenseTypeOptions.find((option) => option.id === Number(expenseType))
        ?.name
    : undefined;
  const expenseTypeSegment = expenseTypeName
    ? { value: expenseTypeName, isMissing: false }
    : { value: translate('sections.typeMissing'), isMissing: true };

  const hasAmount =
    invoiceAmount !== undefined &&
    invoiceAmount !== '' &&
    Number(invoiceAmount) !== 0;
  const currencyIsoCode = currencyId
    ? currencies.find((c) => c.id === currencyId)?.isoCode
    : undefined;
  const amountSegment =
    hasAmount && currencyIsoCode
      ? {
          value: `${formatMoney(Number(invoiceAmount))} ${currencyIsoCode}`,
          isMissing: false
        }
      : { value: translate('sections.amountMissing'), isMissing: true };

  const segments = [
    numberSegment,
    dateSegment,
    expenseTypeSegment,
    amountSegment
  ];
  const hasRealValue = segments.some((segment) => !segment.isMissing);

  const valuesToShow = hasRealValue
    ? segments.filter((segment) => !segment.isMissing).map((s) => s.value)
    : segments.map((s) => s.value);

  return valuesToShow.join(SEPARATOR);
};

interface CommentsAndTagsSummaryParams {
  documentDescription?: string;
  tags?: string[];
}

export const getCommentsAndTagsSummary = (
  { documentDescription, tags }: CommentsAndTagsSummaryParams,
  translate: TranslateFunction
): string => {
  const comment = documentDescription || translate('sections.noComments');

  if (tags && tags.length > 0) {
    return `${comment}${SEPARATOR}${tags.join(', ')}`;
  }

  return comment;
};

interface LineItemsSummaryParams {
  lineItemCount: number;
  lineItemTotalEur: number | null;
  expenseTypeNames: string[];
  untypedCount?: number;
  needsReview: boolean;
}

export const getLineItemsSummary = (
  {
    lineItemCount,
    lineItemTotalEur,
    expenseTypeNames,
    untypedCount = 0,
    needsReview
  }: LineItemsSummaryParams,
  translate: TranslateFunction
): string => {
  const distinctTypeNames = [...new Set(expenseTypeNames.filter(Boolean))];

  let expenseTypesSegment =
    distinctTypeNames.length <= 1
      ? translate('labels.allExpenseTypes', {
          name: distinctTypeNames[0] ?? ''
        })
      : translate('labels.expenseTypesCount', {
          count: String(distinctTypeNames.length)
        });

  if (untypedCount > 0) {
    expenseTypesSegment = `${expenseTypesSegment} + ${translate('labels.plusUntyped')}`;
  }

  const segments = [
    lineItemCount === 1
      ? translate('labels.lineItemSingular', { count: '1' })
      : translate('labels.lineItemsCount', { count: String(lineItemCount) }),
    lineItemTotalEur != null ? `${formatMoney(lineItemTotalEur)} EUR` : '',
    expenseTypesSegment
  ].filter(Boolean);

  if (needsReview) {
    segments.push(translate('labels.totalsNeedReview'));
  }

  return segments.join(SEPARATOR);
};
